<script setup>
import ga4 from '#common/utils/ga4'

const layout = useLayout()
const membershipStore = useMembershipStore()

if (!membershipStore.membershipTypes?.length) {
  membershipStore.fetchMembershipTypes()
}
if (!membershipStore.membershipQuotes?.wineaccess?.length) {
  membershipStore.fetchMembershipQuotes()
}

onMounted(async () => {
  layout.showFooterBoxes = false
  ga4.track('view_clubs')
})
onActivated(() => {
  layout.showFooterBoxes = false
  ga4.track('view_clubs')
})
onBeforeRouteLeave(() => {
  layout.showFooterBoxes = true
})

const membershipTypes = computed(() => membershipStore.membershipTypes)
const membershipQuotes = computed(() => membershipStore.membershipQuotes)
const wineAccessMembershipTypes = computed(() =>
  membershipTypes.value
    .filter((x) => x.store_url && !x.partner_club)
    .sort((a, b) => {
      // First sort by our_pick_stamp (true to the front)
      if (b.our_pick_stamp !== a.our_pick_stamp) {
        return b.our_pick_stamp - a.our_pick_stamp
      }
      // Then sort by position
      return a.position - b.position
    }),
)
const collaboratorMembershipTypes = computed(() =>
  membershipTypes.value
    .filter((x) => x.store_url && x.partner_club)
    .sort(
      (a, b) => b.our_pick_stamp - a.our_pick_stamp || a.position - b.position,
    ),
)
const waitlistMembership = computed(() =>
  membershipTypes.value.filter((m) => m.slug === 'waitlist'),
)
const waitlistPageContent = computed(
  () => waitlistMembership.value?.[0]?.page_content || {},
)
const waitlistDeliveryFrequency = computed(
  () => waitlistMembership.value?.[0]?.delivery_freq_info || '',
)
const waitlistPricingInfo = computed(
  () => waitlistMembership.value?.[0]?.pricing_info || '',
)
const waitlistNextShippingInfo = computed(
  () => waitlistMembership.value?.[0]?.next_shipping_info || '',
)
</script>

<template>
  <div class="b24">
    <div class="wineclub-landing">
      <div class="section clublanding-hero">
        <div class="site-width">
          <div class="row hero text-white">
            <div class="columns hero__copy">
              <h1>A Club for <span class="nowrap">Every Palate</span></h1>
              <p>
                Expertly curated wines and the stories behind them, delivered to
                your door.
              </p>
              <a v-scroll-to="'#waclubs'" href="#waclubs" class="button">
                Find Your Club
              </a>
            </div>
          </div>
        </div>
      </div>
      <div class="section text-center section-mainquotes">
        <div class="site-width">
          <ul class="clublanding-reviews">
            <li>
              <img
                src="~assets/images/clublanding-logo-newyorktimes.svg"
                alt="The New York Times"
              />
              Wirecutter: “It’s clear Wine Access puts a lot of thought into its
              club experience.”
            </li>
            <li>
              <cite>America's Test Kitchen</cite>
              <blockquote>“Best Wine Club”</blockquote>
            </li>
            <li>
              <cite>
                <em>The New York Times</em>
                Wirecutter
              </cite>
              <blockquote>Top Pick: 2023, 2024, 2025</blockquote>
            </li>
            <li>
              <cite>Serious Eats</cite>
              <blockquote>“The Best Wine Subscription”</blockquote>
            </li>
          </ul>
        </div>
      </div>
      <div class="section pt-0 section-benefits">
        <div class="site-width">
          <div class="wineclub-landing_checklist-image">
            <img
              src="~assets/images/clublanding-photo1.webp"
              alt="Wine bottles and wine glass display"
            />
          </div>
          <div class="f22d">
            <div class="row">
              <h2>We don’t just love wine—we live to share it.</h2>
              <div class="columns large-offset-1 large-10 p-0 end">
                <p>
                  After tasting 20,000 wines every year all over the world, we
                  put some of our favorite bottles—some of the most inspiring,
                  some of the most tightly allocated—into our wine clubs.
                  Whether it’s a classic from your favorite region or a rare
                  find to whisk you outside your comfort zone, our clubs are
                  packed with incredible value. Being a member is like being on
                  the mailing list at top wineries worldwide—and we’re so
                  confident in our selections that we’ll issue you a credit for
                  any bottle that fails to impress.
                </p>
                <p class="text-center wineclub-landing__checklist-header">
                  <strong>All members get:</strong>
                </p>
              </div>
            </div>
            <div class="wineclub-landing__checklist">
              <ul class="checkmark-round">
                <li>New wines in every box</li>
                <li>Complimentary shipping</li>
                <li>Easily add wines to your shipments</li>
              </ul>
              <ul class="checkmark-round">
                <li>Tasting and pairing cards</li>
                <li>Exclusive video tastings</li>
                <li>10% off all Wine Access purchases</li>
              </ul>
            </div>

            <div class="row">
              <p class="text-center m-0">
                It’s easy to pause shipments, and you can cancel anytime.
              </p>
            </div>
          </div>
        </div>
        <div class="shade clublanding-reviews-mobile">
          <ul class="clublanding-reviews secondary">
            <li>
              <cite>America's Test Kitchen</cite>
              <blockquote>"Best Wine Club"</blockquote>
            </li>
            <li>
              <cite>
                <em>The New York Times</em>
                Wirecutter
              </cite>
              <blockquote>Top Pick: 2023, 2024, 2025</blockquote>
            </li>
            <li>
              <cite>Serious Eats</cite>
              <blockquote>"The Best Wine Subscription"</blockquote>
            </li>
          </ul>
        </div>
      </div>
      <div id="waclubs" class="section pt-0">
        <h2>Wine Access Clubs</h2>
        <WineClubCarousel
          id-prefix="waclub"
          :membership-types="wineAccessMembershipTypes"
          :quotes="membershipQuotes.wineaccess"
        />
      </div>
      <div class="section bg-shade-warm featured-club">
        <div class="site-width">
          <div class="copyblock text-center">
            <h2>Featured Club</h2>
            <img
              src="~assets/images/clublanding-feeaturedlogo-waitlist.svg"
              alt="The Waitlist"
              class="logo"
            />
            <hr />
            <div class="featured-club__intro">
              <p class="featured-club__intro__header">
                <strong
                  >An Exclusive Subscription to World-class Wines Curated by
                  Culinary Icons</strong
                >
              </p>
              <p>
                James Beard awards, Michelin stars, the World’s 50 Best—we’re
                ushering you into the most exclusive kitchens and dining rooms
                in the world.
              </p>
              <p>
                <nuxt-link to="/waitlist/" class="longarrow-black underline">
                  Learn More
                </nuxt-link>
              </p>
            </div>
          </div>
          <div class="row grid-masonry grid-masonry--masonry1">
            <div class="columns">
              <img
                v-if="waitlistPageContent.restaurant_image"
                :src="waitlistPageContent.restaurant_image"
                alt=""
              />
              <img
                v-else
                src="~/assets/images/club-waitlist-photo3.webp"
                alt="Chefs Emeril Lagasse and E.J. Lagasse—named Chef Patron of Emeril’s in 2022"
              />
            </div>
            <div class="columns grid-masonry__copy shade">
              <div class="column1">
                <h3 class="f17 mb-6">Upcoming Shipment</h3>
                <h4 v-if="waitlistPageContent.restaurant_name" class="f17">
                  {{ waitlistPageContent.restaurant_name }}
                </h4>
                <h4 v-else class="f17">Emeril's</h4>
                <p
                  v-if="waitlistPageContent.restaurant_description"
                  v-html="waitlistPageContent.restaurant_description"
                ></p>
                <p v-else>
                  The New Orleans flagship of chef Emeril Lagasse has been
                  delighting diners for 34 years, ever since he saw the
                  potential in a dimly lit industrial neighborhood full of
                  abandoned buildings. Over 34 years, the Emeril’s wine program
                  has earned high honors: It first took home
                  <em>Wine Spectator’s</em>
                  coveted Grand Award in 2000, cementing the list as one of the
                  world’s best.
                </p>
              </div>
              <div class="column2">
                <p class="mb-6">
                  <strong>Past Shipments</strong>
                </p>
                <ul class="f17">
                  <li
                    v-if="waitlistPageContent.past_shipment_text_1"
                    v-html="waitlistPageContent.past_shipment_text_1"
                  ></li>
                  <li v-else>
                    <strong>Atomix</strong>, #6 on World’s 50 Best (highest
                    ranked US)
                  </li>
                  <li
                    v-if="waitlistPageContent.past_shipment_text_2"
                    v-html="waitlistPageContent.past_shipment_text_2"
                  ></li>
                  <li v-else>
                    <strong>Restaurant DANIEL</strong>, Daniel Boulud’s
                    two-Michelin starred flagship
                  </li>
                </ul>
                <br />
                <p class="mb-6">
                  <strong>Frequency and Cost</strong>
                </p>
                <ul class="f17">
                  <li>
                    {{ waitlistDeliveryFrequency }}
                  </li>
                  <li>
                    {{ waitlistPricingInfo }}
                  </li>
                  <li>Next Shipment: {{ waitlistNextShippingInfo }}</li>
                </ul>
              </div>
            </div>
            <div class="columns">
              <img
                v-if="waitlistPageContent.past_shipment_image_1"
                :src="waitlistPageContent.past_shipment_image_1"
                alt=""
              />
              <img
                v-else
                src="~/assets/images/club-waitlist-photo4.webp"
                alt="Chef Junghyun Park"
              />
            </div>
            <div class="columns">
              <img
                v-if="waitlistPageContent.past_shipment_image_2"
                :src="waitlistPageContent.past_shipment_image_2"
                alt=""
              />
              <img
                v-else
                src="~/assets/images/club-waitlist-photo5.webp"
                alt="Chef Daniel Boulud"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="section">
        <h2>Club Collaborations</h2>
        <WineClubCarousel
          id-prefix="collaboratorclub"
          :membership-types="collaboratorMembershipTypes"
          :quotes="membershipQuotes.collaborators"
        />
      </div>
      <div class="section pt-0 pb-0">
        <div class="row feature-blocks p-0 mt-0">
          <div
            class="columns feature-block feature-block--gray px-md-0 bg-share text-left w-full"
          >
            <div class="site-width">
              <div class="bg-share__placement">
                <h2>Share Your Love of Wine</h2>
                <nuxt-link to="/wine-club/gifting/" class="link-overlay">
                  <span class="sr-only">Gift a Wine Club</span>
                </nuxt-link>
                <p class="large">
                  When you gift a club, you can customize the number of
                  shipments, frequency, and more.
                </p>
                <p class="feature-block__cta">
                  <span class="underline nowrap longarrow-black"
                    >Gift a Club</span
                  >
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="section section-faq">
        <FAQ faq-slug="club-landing-faq" />
      </div>
      <div class="section section-customerservice text-center pt-0">
        Still have questions? We’re here to help.<br />
        Contact us at
        <CustomerServicePhoneLink
          :with-hours-short="true"
          :include-from-word="false"
        />
        or
        <CustomerServiceEmailLink />
      </div>
    </div>
  </div>
</template>
